import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { cvApi, jobsApi, userApi, messagesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Upload, Search, Star, Briefcase, FileText, ExternalLink, 
    Loader2, CheckCircle, XCircle, Sparkles, TrendingUp, User,
    LogOut, Heart, Clock, Trash2, Bell, Send
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Google Custom Search Component
function GoogleCustomSearch() {
    const containerRef = useRef(null);
    
    useEffect(() => {
        // Load Google CSE script
        const script = document.createElement('script');
        script.src = 'https://cse.google.com/cse.js?cx=b21844108a100497a';
        script.async = true;
        document.head.appendChild(script);
        
        // Render the search element when script loads
        script.onload = () => {
            if (containerRef.current && window.google && window.google.search) {
                window.google.search.cse.element.render({
                    div: containerRef.current,
                    tag: 'search'
                });
            }
        };
        
        return () => {
            // Cleanup script on unmount
            const existingScript = document.querySelector('script[src*="cse.google.com"]');
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, []);
    
    return (
        <div className="gcse-container">
            <div className="gcse-search" ref={containerRef}></div>
            <style>{`
                .gcse-container {
                    width: 100%;
                }
                .gsc-control-cse {
                    background-color: transparent !important;
                    border: none !important;
                    padding: 0 !important;
                }
                .gsc-search-box {
                    margin-bottom: 0 !important;
                }
                .gsc-input-box {
                    background-color: rgba(0, 0, 0, 0.2) !important;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                    border-radius: 4px !important;
                }
                .gsc-input {
                    background-color: transparent !important;
                    color: #fff !important;
                    font-family: 'JetBrains Mono', monospace !important;
                }
                .gsc-search-button-v2 {
                    background-color: #DAFF00 !important;
                    border: none !important;
                    border-radius: 4px !important;
                    padding: 10px 16px !important;
                }
                .gsc-search-button-v2 svg {
                    fill: #000 !important;
                }
                .gsc-results-wrapper-overlay {
                    background-color: #0a0a0a !important;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                }
                .gsc-webResult.gsc-result {
                    background-color: rgba(0, 0, 0, 0.3) !important;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                    border-radius: 4px !important;
                    margin-bottom: 8px !important;
                    padding: 12px !important;
                }
                .gs-title, .gs-title * {
                    color: #DAFF00 !important;
                }
                .gs-snippet {
                    color: #a1a1aa !important;
                }
                .gsc-url-top, .gsc-url-bottom {
                    color: #00f0ff !important;
                }
                .gsc-tabHeader.gsc-tabhActive {
                    background-color: #DAFF00 !important;
                    color: #000 !important;
                }
                .gsc-results .gsc-cursor-box {
                    margin-top: 16px !important;
                }
                .gsc-cursor-page {
                    background-color: rgba(218, 255, 0, 0.1) !important;
                    color: #DAFF00 !important;
                    border: 1px solid rgba(218, 255, 0, 0.3) !important;
                }
                .gsc-cursor-current-page {
                    background-color: #DAFF00 !important;
                    color: #000 !important;
                }
            `}</style>
        </div>
    );
}

export default function Dashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [cvs, setCvs] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [stats, setStats] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedCv, setSelectedCv] = useState(null);
    const [unreadMessages, setUnreadMessages] = useState(0);

    const loadData = useCallback(async () => {
        try {
            const [cvsData, statsData, unreadData] = await Promise.all([
                cvApi.list(),
                userApi.getStats(),
                messagesApi.getUnreadCount().catch(() => ({ unread_count: 0 }))
            ]);
            setCvs(cvsData);
            setStats(statsData);
            setUnreadMessages(unreadData.unread_count || 0);
            if (cvsData.length > 0) {
                setSelectedCv(cvsData[0]);
            }
        } catch (error) {
            console.error('Error loading data:', error);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Generate suggestion keywords when CV is selected
    useEffect(() => {
        if (selectedCv) {
            // Extract keywords from CV for search suggestions
            const extractKeywords = (cv) => {
                const keywords = [];
                const role = cv?.primary_sector && cv.primary_sector !== 'unknown' ? cv.primary_sector : '';
                
                // Get industry/protocol keywords
                let industryTerms = cv?.sector_keywords || [];
                
                // If no sector_keywords, extract from skills
                if (industryTerms.length === 0 && cv?.skills?.length > 0) {
                    const highValueTerms = ['DeFi', 'DEX', 'NFT', 'perpetuals', 'trading', 'liquidity', 
                                          'AMM', 'lending', 'staking', 'GameFi', 'derivatives', 'RWA'];
                    
                    industryTerms = cv.skills.filter(skill => 
                        highValueTerms.some(term => skill.toLowerCase().includes(term.toLowerCase()))
                    ).map(s => {
                        for (const term of highValueTerms) {
                            if (s.toLowerCase().includes(term.toLowerCase())) {
                                return term;
                            }
                        }
                        return s;
                    }).slice(0, 3);
                    
                    industryTerms = [...new Set(industryTerms)];
                }
                
                // Build search phrases
                if (cv?.job_search_terms?.length > 0) {
                    keywords.push(...cv.job_search_terms.slice(0, 2));
                }
                
                if (industryTerms.length > 0 && role) {
                    keywords.push(`Web3 ${industryTerms[0]} ${role}`);
                    if (industryTerms.length > 1) {
                        keywords.push(`${industryTerms[1]} ${role}`);
                    }
                } else if (industryTerms.length > 0) {
                    keywords.push(`Web3 ${industryTerms[0]}`);
                } else if (role) {
                    keywords.push(`Web3 ${role}`);
                }
                
                // Add individual terms
                industryTerms.slice(0, 2).forEach(term => {
                    if (!keywords.some(k => k.toLowerCase().includes(term.toLowerCase()))) {
                        keywords.push(term);
                    }
                });
                
                return [...new Set(keywords)].slice(0, 4);
            };
            
            const keywords = extractKeywords(selectedCv);
            setSuggestedKeywords(keywords);
        }
    }, [selectedCv]);

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        if (!validTypes.includes(file.type)) {
            toast.error('Only PDF and DOCX files are supported');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        // Simulate progress
        const progressInterval = setInterval(() => {
            setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 500);

        try {
            const result = await cvApi.upload(file);
            setUploadProgress(100);
            toast.success('CV uploaded and analyzed successfully!');
            setCvs(prev => [result, ...prev]);
            setSelectedCv(result);
            
            // Search for matching jobs
            handleSearchJobs(result.cv_id);
        } catch (error) {
            toast.error(error.message || 'Upload failed');
        } finally {
            clearInterval(progressInterval);
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const [noResultsFromAI, setNoResultsFromAI] = useState(false);
    const [suggestedKeywords, setSuggestedKeywords] = useState([]);
    const [deletingCvId, setDeletingCvId] = useState(null);

    const handleDeleteCv = async (cvId, e) => {
        e.stopPropagation(); // Prevent selecting the CV when clicking delete
        
        // Don't allow deleting the last CV
        if (cvs.length <= 1) {
            toast.error('You must keep at least one CV');
            return;
        }
        
        setDeletingCvId(cvId);
        try {
            await cvApi.delete(cvId);
            setCvs(prev => prev.filter(cv => cv.cv_id !== cvId));
            
            // If we deleted the selected CV, select the first remaining one
            if (selectedCv?.cv_id === cvId) {
                const remaining = cvs.filter(cv => cv.cv_id !== cvId);
                setSelectedCv(remaining[0] || null);
            }
            
            toast.success('CV deleted');
        } catch (error) {
            toast.error('Failed to delete CV');
        } finally {
            setDeletingCvId(null);
        }
    };

    const handleManualSearch = async (query = null) => {
        const searchTerm = query || searchQuery;
        if (!searchTerm?.trim()) {
            toast.error('Please enter a search query');
            return;
        }
        setIsSearching(true);
        setNoResultsFromAI(false);
        setSuggestedKeywords([]); // Clear AI suggestions during manual search
        try {
            // Direct SerpAPI search - no CV matching, just keyword search
            const result = await jobsApi.search(searchTerm);
            setJobs(result.jobs || []);
            if (result.jobs?.length > 0) {
                toast.success(`Found ${result.jobs.length} jobs for "${searchTerm}"`);
            } else {
                toast.info('No jobs found. Try different keywords.');
            }
        } catch (error) {
            console.error('Search error:', error);
            if (error.message?.includes('authenticated') || error.message?.includes('401')) {
                toast.error('Session expired. Please refresh and log in again.');
            } else {
                toast.error('Job search failed. Please try again.');
            }
        } finally {
            setIsSearching(false);
        }
    };

    const buildSearchKeywords = (cv) => {
        const keywords = [];
        
        // Get role type (product, marketing, developer, etc.)
        const role = cv?.primary_sector && cv.primary_sector !== 'unknown' ? cv.primary_sector : '';
        
        // Get industry/protocol keywords (DeFi, DEX, perpetuals, etc.)
        let industryTerms = cv?.sector_keywords || [];
        
        // If no sector_keywords, extract from skills
        if (industryTerms.length === 0 && cv?.skills?.length > 0) {
            const highValueTerms = ['DeFi', 'DEX', 'NFT', 'perpetuals', 'trading', 'liquidity', 
                'AMM', 'lending', 'staking', 'yield', 'derivatives', 'RWA', 'GameFi',
                'L2', 'rollup', 'bridge', 'oracle', 'Solidity', 'smart contracts'];
            
            industryTerms = cv.skills.filter(skill => 
                highValueTerms.some(term => skill.toLowerCase().includes(term.toLowerCase()))
            ).map(s => {
                // Extract the key term from longer skill descriptions
                for (const term of highValueTerms) {
                    if (s.toLowerCase().includes(term.toLowerCase())) {
                        return term;
                    }
                }
                return s;
            }).slice(0, 3);
            
            // Dedupe
            industryTerms = [...new Set(industryTerms)];
        }
        
        // First: Use job_search_terms if available (AI-generated search phrases)
        if (cv?.job_search_terms?.length > 0) {
            keywords.push(...cv.job_search_terms.slice(0, 2));
        }
        
        // Second: Build "Web3 [industry] [role]" style queries
        if (industryTerms.length > 0 && role) {
            // Create combinations like "Web3 DEX product", "DeFi product manager"
            const topIndustry = industryTerms[0];
            keywords.push(`Web3 ${topIndustry} ${role}`);
            
            if (industryTerms.length > 1) {
                keywords.push(`${industryTerms[1]} ${role}`);
            }
        } else if (industryTerms.length > 0) {
            // Just industry terms with Web3 prefix
            keywords.push(`Web3 ${industryTerms[0]}`);
            if (industryTerms.length > 1) {
                keywords.push(industryTerms[1]);
            }
        } else if (role) {
            // Fallback to role with Web3
            keywords.push(`Web3 ${role}`);
        }
        
        // Add individual high-value industry terms for button suggestions
        industryTerms.slice(0, 2).forEach(term => {
            if (!keywords.some(k => k.toLowerCase().includes(term.toLowerCase()))) {
                keywords.push(term);
            }
        });
        
        return [...new Set(keywords)].slice(0, 4); // Unique, max 4
    };

    const handleSearchJobs = async (cvId = null) => {
        setIsSearching(true);
        setNoResultsFromAI(false);
        setSuggestedKeywords([]);
        try {
            let result;
            const targetCv = cvId ? cvs.find(c => c.cv_id === cvId) || selectedCv : selectedCv;
            
            if (cvId || selectedCv) {
                result = await jobsApi.matchForCV(cvId || selectedCv.cv_id);
                
                // Build keywords for suggestion buttons regardless of results
                const keywords = buildSearchKeywords(targetCv);
                setSuggestedKeywords(keywords);
                
                // If AI matching returned few or no results, auto-search with CV keywords
                if (!result.jobs || result.jobs.length < 3) {
                    if (keywords.length > 0) {
                        // Use the first (best) keyword for auto-search
                        const autoSearchQuery = keywords[0];
                        setSearchQuery(autoSearchQuery);
                        
                        if (result.jobs?.length === 0) {
                            toast.info(`No exact matches. Searching for "${autoSearchQuery}"...`);
                        }
                        
                        // Auto-run manual search with extracted keywords
                        const fallbackResult = await jobsApi.search(autoSearchQuery);
                        
                        // Combine results - AI matches first, then fallback
                        const combinedJobs = [...(result.jobs || [])];
                        const existingUrls = new Set(combinedJobs.map(j => j.url));
                        
                        for (const job of (fallbackResult.jobs || [])) {
                            if (!existingUrls.has(job.url)) {
                                combinedJobs.push(job);
                            }
                        }
                        
                        setJobs(combinedJobs);
                        
                        if (combinedJobs.length > 0) {
                            toast.success(`Found ${combinedJobs.length} jobs`);
                        } else {
                            setNoResultsFromAI(true);
                            toast.info('No jobs found. Try different keywords.');
                        }
                    } else {
                        setJobs(result.jobs || []);
                        if (!result.jobs?.length) {
                            setNoResultsFromAI(true);
                        }
                    }
                } else {
                    toast.success(`Found ${result.jobs.length} matching jobs`);
                    setJobs(result.jobs || []);
                }
            } else {
                result = await jobsApi.search(searchQuery || 'web3 developer jobs');
                if (result.jobs?.length > 0) {
                    toast.success(`Found ${result.jobs.length} jobs`);
                }
                setJobs(result.jobs || []);
            }
        } catch (error) {
            toast.error('Job search failed');
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <div className="min-h-screen bg-void page-enter">
            <div className="noise-overlay" />

            {/* Header */}
            <header className="sticky top-0 z-20 glass border-b border-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-acid rounded-sm flex items-center justify-center">
                            <span className="text-black font-display font-bold text-lg">N</span>
                        </div>
                        <span className="font-display text-xl text-white hidden sm:block">NODE HUNTER</span>
                    </Link>

                    <nav className="flex items-center gap-6">
                        <Link 
                            to="/messages" 
                            className="text-muted-foreground hover:text-acid transition-colors flex items-center gap-2 relative"
                            data-testid="nav-messages"
                        >
                            <Bell className="w-4 h-4" strokeWidth={1.5} />
                            {unreadMessages > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-neon-red text-white text-[10px] rounded-full flex items-center justify-center font-mono">
                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                </span>
                            )}
                            <span className="hidden sm:inline font-mono text-xs uppercase tracking-wider">Messages</span>
                        </Link>
                        <Link 
                            to="/favorites" 
                            className="text-muted-foreground hover:text-acid transition-colors flex items-center gap-2"
                            data-testid="nav-favorites"
                        >
                            <Heart className="w-4 h-4" strokeWidth={1.5} />
                            <span className="hidden sm:inline font-mono text-xs uppercase tracking-wider">Favorites</span>
                        </Link>
                        <Link 
                            to="/applied" 
                            className="text-muted-foreground hover:text-acid transition-colors flex items-center gap-2"
                            data-testid="nav-applied"
                        >
                            <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                            <span className="hidden sm:inline font-mono text-xs uppercase tracking-wider">Applied</span>
                        </Link>
                        <Link 
                            to="/profile" 
                            className="text-muted-foreground hover:text-acid transition-colors flex items-center gap-2"
                            data-testid="nav-profile"
                        >
                            <User className="w-4 h-4" strokeWidth={1.5} />
                            <span className="hidden sm:inline font-mono text-xs uppercase tracking-wider">Profile</span>
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={logout}
                            className="text-muted-foreground hover:text-neon-red"
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                    </nav>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h1 className="font-display text-3xl lg:text-4xl font-bold text-white mb-2">
                        Welcome back, <span className="text-acid">{user?.name?.split(' ')[0] || 'Hunter'}</span>
                    </h1>
                    <p className="text-muted-foreground">
                        Upload your CV and discover your perfect Web3 opportunity
                    </p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card className="bg-void-paper border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-acid/10 border border-acid/30 rounded-sm flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-bold text-white">{stats?.cvs_uploaded || 0}</div>
                                    <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">CVs</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-void-paper border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-cyber-purple/10 border border-cyber-purple/30 rounded-sm flex items-center justify-center">
                                    <Search className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-bold text-white">{jobs.length}</div>
                                    <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Matches</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-void-paper border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-neon-cyan/10 border border-neon-cyan/30 rounded-sm flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-neon-cyan" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-bold text-white">{stats?.favorites || 0}</div>
                                    <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Saved</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-void-paper border-border">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-neon-red/10 border border-neon-red/30 rounded-sm flex items-center justify-center">
                                    <Briefcase className="w-5 h-5 text-neon-red" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-bold text-white">{stats?.total_applied || 0}</div>
                                    <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Applied</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Grid */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left Column - CV Upload & List */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Upload Card */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Upload className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                    Upload CV
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {cvs.length >= 5 && !user?.is_admin ? (
                                    <div className="border-2 border-dashed border-amber-500/30 rounded-sm p-8 text-center bg-amber-500/5">
                                        <XCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" strokeWidth={1.5} />
                                        <p className="text-amber-500 text-sm font-medium mb-1">
                                            CV Limit Reached
                                        </p>
                                        <p className="text-muted-foreground text-xs font-mono">
                                            Maximum 5 CVs allowed. Delete an old CV to upload a new one.
                                        </p>
                                    </div>
                                ) : (
                                    <label 
                                        className="block border-2 border-dashed border-border hover:border-acid/50 rounded-sm p-8 text-center cursor-pointer transition-colors group"
                                        data-testid="cv-upload-area"
                                    >
                                        <input 
                                            type="file" 
                                            accept=".pdf,.docx" 
                                            onChange={handleFileUpload}
                                            className="hidden"
                                            disabled={isUploading}
                                            data-testid="cv-upload-input"
                                        />
                                        {isUploading ? (
                                            <div>
                                                <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-3" />
                                                <p className="text-muted-foreground text-sm mb-2">Analyzing your CV...</p>
                                                <Progress value={uploadProgress} className="h-1" />
                                            </div>
                                        ) : (
                                            <>
                                                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3 group-hover:text-acid transition-colors" strokeWidth={1.5} />
                                                <p className="text-muted-foreground text-sm mb-1">
                                                    Drop PDF or DOCX here
                                                </p>
                                                <p className="text-muted-foreground/50 text-xs font-mono">
                                                    or click to browse
                                                </p>
                                            </>
                                        )}
                                    </label>
                                )}
                                
                                {/* Data Processing Notice */}
                                <p className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground/70 leading-relaxed">
                                    By uploading your CV, you consent to the processing of your personal data in accordance with applicable data protection laws (including GDPR). Employers will be able to search your CV based on their own job descriptions and contact you regarding any opportunities. By submitting your CV you give permission for employers to contact you on the platform. You can request deletion of your data at any time.
                                </p>
                            </CardContent>
                        </Card>

                        {/* CV List */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                                        Your CVs
                                    </span>
                                    {!user?.is_admin && (
                                        <span className="font-mono text-xs text-muted-foreground">
                                            {cvs.length}/5
                                        </span>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {cvs.length === 0 ? (
                                    <p className="text-muted-foreground text-sm text-center py-8">
                                        No CVs uploaded yet
                                    </p>
                                ) : (
                                    <ScrollArea className="h-[300px]">
                                        <div className="space-y-2">
                                            {cvs.map((cv, index) => (
                                                <div
                                                    key={cv.cv_id}
                                                    className={`p-3 rounded-sm border cursor-pointer transition-all group ${
                                                        selectedCv?.cv_id === cv.cv_id
                                                            ? 'border-acid bg-acid/5'
                                                            : 'border-border hover:border-acid/50'
                                                    }`}
                                                    onClick={() => setSelectedCv(cv)}
                                                    data-testid={`cv-item-${cv.cv_id}`}
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <p className="text-white text-sm font-medium truncate">
                                                                    {cv.filename}
                                                                </p>
                                                                {index === 0 && (
                                                                    <Badge className="font-mono text-[10px] bg-acid/10 text-acid border-acid/30 px-1.5 py-0">
                                                                        Latest
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <p className="text-muted-foreground text-xs font-mono mt-1">
                                                                {cv.skills?.length || 0} skills detected
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            {selectedCv?.cv_id === cv.cv_id && (
                                                                <CheckCircle className="w-4 h-4 text-acid flex-shrink-0" strokeWidth={1.5} />
                                                            )}
                                                            {/* Delete button - always visible when multiple CVs exist */}
                                                            {cvs.length > 1 && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={(e) => handleDeleteCv(cv.cv_id, e)}
                                                                    disabled={deletingCvId === cv.cv_id}
                                                                    className="h-6 w-6 p-0 text-muted-foreground hover:text-neon-red hover:bg-neon-red/10 transition-colors"
                                                                    data-testid={`delete-cv-${cv.cv_id}`}
                                                                >
                                                                    {deletingCvId === cv.cv_id ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                    ) : (
                                                                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>

                        {/* Selected CV Skills */}
                        {selectedCv && (
                            <Card className="bg-void-paper border-border">
                                <CardHeader className="pb-3">
                                    <CardTitle className="font-display text-lg flex items-center gap-2">
                                        <Sparkles className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                        Detected Skills
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedCv.skills?.slice(0, 15).map((skill, idx) => (
                                            <Badge 
                                                key={idx}
                                                variant="outline"
                                                className="font-mono text-xs border-acid/30 text-acid bg-acid/5"
                                            >
                                                {skill}
                                            </Badge>
                                        ))}
                                        {selectedCv.skills?.length > 15 && (
                                            <Badge variant="outline" className="font-mono text-xs border-border text-muted-foreground">
                                                +{selectedCv.skills.length - 15} more
                                            </Badge>
                                        )}
                                    </div>
                                    {selectedCv.summary && (
                                        <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
                                            {selectedCv.summary}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Right Column - Job Search & Results */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Search Card */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Search className="w-5 h-5 text-neon-cyan" strokeWidth={1.5} />
                                    Find Jobs
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {/* AI Matching Section */}
                                {selectedCv && (
                                    <div className="mb-4 pb-4 border-b border-border">
                                        <p className="text-muted-foreground text-xs font-mono uppercase tracking-wider mb-2">
                                            AI-Powered Matching
                                        </p>
                                        <Button
                                            onClick={() => handleSearchJobs(selectedCv.cv_id)}
                                            disabled={isSearching}
                                            className="w-full bg-acid text-black font-mono uppercase tracking-wider text-sm hover:bg-acid-hover"
                                            data-testid="match-cv-btn"
                                        >
                                            {isSearching ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                            )}
                                            Find Matches for &ldquo;{selectedCv.filename}&rdquo;
                                        </Button>
                                    </div>
                                )}
                                
                                {/* Google Custom Search - ATS Job Search */}
                                <div className="mt-4">
                                    <p className="text-muted-foreground text-xs font-mono uppercase tracking-wider mb-3">
                                        Search ATS Job Boards
                                    </p>
                                    <GoogleCustomSearch />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Job Results */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="font-display text-lg flex items-center gap-2">
                                        <Briefcase className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                        Job Matches
                                    </CardTitle>
                                    {jobs.length > 0 && (
                                        <Badge className="font-mono text-xs bg-acid/10 text-acid border-acid/30">
                                            {jobs.length} results
                                        </Badge>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {isSearching ? (
                                    <div className="text-center py-12">
                                        <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-3" />
                                        <p className="text-muted-foreground text-sm">Searching ATS systems...</p>
                                    </div>
                                ) : jobs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
                                        <p className="text-muted-foreground text-sm mb-2">No jobs found yet</p>
                                        <p className="text-muted-foreground/50 text-xs">
                                            Upload a CV and use AI matching, or search manually
                                        </p>
                                    </div>
                                ) : (
                                    <ScrollArea className="h-[2000px]">
                                        <div className="space-y-3 pr-4">
                                            {jobs.map((job, idx) => (
                                                <JobCard key={job.job_id || idx} job={job} />
                                            ))}
                                            {jobs.length >= 10 && (
                                                <p className="text-center text-muted-foreground/50 text-xs py-4">
                                                    Showing {jobs.length} job matches
                                                </p>
                                            )}
                                        </div>
                                    </ScrollArea>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
}

function JobCard({ job }) {
    const [isSaving, setIsSaving] = useState(false);
    const [isApplying, setIsApplying] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const { favoritesApi } = await import('../lib/api');
            await favoritesApi.add(job);
            toast.success('Job saved to favorites');
        } catch (error) {
            toast.error(error.message || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    const handleApply = async () => {
        setIsApplying(true);
        try {
            const { appliedApi } = await import('../lib/api');
            await appliedApi.add(job);
            toast.success('Marked as applied');
        } catch (error) {
            toast.error(error.message || 'Failed to mark as applied');
        } finally {
            setIsApplying(false);
        }
    };

    const handleJobClick = async () => {
        // Track the click in the background (non-blocking)
        try {
            const { jobsApi } = await import('../lib/api');
            jobsApi.trackClick(job).catch(err => console.log('Click tracking failed:', err));
        } catch (error) {
            // Silent fail - don't block the user
        }
    };

    const handleShareToTelegram = () => {
        // Create a shareable message
        const message = `🔥 ${job.title}\n\n🏢 ${job.company}${job.location ? `\n📍 ${job.location}` : ''}${job.salary ? `\n💰 ${job.salary}` : ''}\n\nApply here: ${job.url}\n\n📲 Found via Node Hunter`;
        
        // Use Telegram share URL
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(job.url)}&text=${encodeURIComponent(message)}`;
        window.open(telegramUrl, '_blank', 'width=600,height=500');
        toast.success('Opening Telegram to share...');
    };

    const getFitLabelStyle = (label) => {
        if (label?.includes('strong')) return 'text-acid border-acid/30 bg-acid/10';
        if (label?.includes('good')) return 'text-neon-cyan border-neon-cyan/30 bg-neon-cyan/10';
        return 'text-amber-400 border-amber-400/30 bg-amber-400/10';
    };

    const getFitBorderColor = (label) => {
        if (label?.includes('strong')) return 'border-l-acid';
        if (label?.includes('good')) return 'border-l-neon-cyan';
        return 'border-l-amber-400';
    };

    // Get company logo URL
    const getCompanyLogoUrl = (company) => {
        if (!company) return null;
        // Extract domain from company name or use company name directly
        const cleanName = company.toLowerCase().replace(/[^a-z0-9]/g, '');
        return `https://img.logo.dev/${cleanName}.com?token=pk_X-1ZO13GSgeOoUrIuJ6GMQ`;
    };

    const logoUrl = getCompanyLogoUrl(job.company);
    const fallbackLogo = `https://ui-avatars.com/api/?name=${encodeURIComponent(job.company || 'C')}&background=random&size=48`;

    return (
        <div 
            className={`p-4 bg-void-subtle border border-border border-l-4 ${getFitBorderColor(job.fit_label)} rounded-sm card-hover group ${job.is_top_match ? 'ring-1 ring-acid/20' : ''}`}
            data-testid={`job-card-${job.job_id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3 flex-1 min-w-0">
                    {/* Company Logo */}
                    <div className="flex-shrink-0">
                        <img
                            src={logoUrl}
                            alt={job.company}
                            className="w-12 h-12 rounded-sm border border-border object-contain bg-white/5"
                            onError={(e) => { e.target.src = fallbackLogo; }}
                        />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                        {/* Fit Label Badge - Top */}
                        <div className="mb-2 flex items-center gap-2">
                            {job.fit_label && (
                                <Badge className={`font-mono text-xs ${getFitLabelStyle(job.fit_label)}`}>
                                    {job.fit_label}
                                </Badge>
                            )}
                            {job.is_top_match && (
                                <Badge className="font-mono text-xs bg-acid/20 text-acid border-acid/40">
                                    ★ Top Match
                                </Badge>
                            )}
                        </div>
                        
                        {/* Job Title - Prominent */}
                        <h3 className="font-display text-lg font-bold text-white mb-1 group-hover:text-acid transition-colors">
                            {job.title}
                        </h3>
                        
                        {/* Company Name */}
                        <p className="text-acid text-sm font-semibold mb-2">
                            {job.company}
                        </p>
                        
                        {/* Match Reason - Why this job matches */}
                        {job.match_reason && (
                            <p className="text-neon-cyan/80 text-xs italic mb-2 leading-relaxed">
                                💡 {job.match_reason}
                            </p>
                        )}
                        
                        {/* Missing Skills - What would make you stronger */}
                        {job.missing_skills && job.missing_skills.length > 0 && (
                            <div className="mb-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded-sm">
                                <p className="text-amber-400/90 text-xs">
                                    <span className="font-semibold">Strengthen your CV:</span> Consider adding {job.missing_skills.slice(0, 3).join(', ')} experience
                                </p>
                            </div>
                        )}
                        
                        {/* Low Match Reason */}
                        {job.low_match_reason && (
                            <p className="text-amber-400/70 text-xs mb-2">
                                📝 {job.low_match_reason}
                            </p>
                        )}
                        
                        {/* Job Description */}
                    <p className="text-muted-foreground/70 text-sm line-clamp-2 mb-3">
                        {job.description}
                    </p>
                    
                    {/* Location, Salary & Date Posted */}
                    <div className="flex flex-wrap items-center gap-3 text-xs font-mono">
                        {job.location && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                                📍 {job.location}
                            </span>
                        )}
                        {job.salary && (
                            <span className="text-acid font-semibold">
                                💰 {job.salary}
                            </span>
                        )}
                        {job.date_posted && (
                            <span className="text-cyber-purple">
                                🕐 {job.date_posted}
                            </span>
                        )}
                    </div>
                    
                    {/* Skills */}
                    {job.skills_required && job.skills_required.length > 0 && (
                        <div className="mt-2 text-xs text-muted-foreground/50 font-mono">
                            {job.skills_required.slice(0, 4).join(' • ')}
                        </div>
                    )}
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="text-muted-foreground hover:text-acid"
                        data-testid={`save-job-${job.job_id}`}
                        title="Save to favorites"
                    >
                        {isSaving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Heart className="w-4 h-4" strokeWidth={1.5} />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleApply}
                        disabled={isApplying}
                        className="text-muted-foreground hover:text-cyber-purple"
                        data-testid={`apply-job-${job.job_id}`}
                        title="Mark as applied"
                    >
                        {isApplying ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleShareToTelegram}
                        className="text-muted-foreground hover:text-[#0088cc]"
                        data-testid={`share-telegram-${job.job_id}`}
                        title="Share to Telegram"
                    >
                        <Send className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleJobClick}
                        className="inline-flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-neon-cyan transition-colors"
                        data-testid={`view-job-${job.job_id}`}
                        title="Open job listing"
                    >
                        <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                </div>
            </div>
        </div>
    );
}
